// src/pages/ProductDetail.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  limit,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import "./ProductDetail.css";

function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [mainImg, setMainImg] = useState("");
  const [selectedSize, setSelectedSize] = useState(null);
  const [qty, setQty] = useState(1);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    async function load() {
      try {
        const ref = doc(db, "products", id);
        const snap = await getDoc(ref);

        if (!snap.exists()) {
          setProduct(null);
          return;
        }

        const data = snap.data();
        setProduct(data);

        // images[] > image > placeholder
        const imgs = data.images || (data.image ? [data.image] : []);
        setMainImg(imgs[0] || "/images/placeholder.jpg");

        // related products: same category
        if (data.category) {
          try {
            const qRel = query(
              collection(db, "products"),
              where("category", "==", data.category),
              limit(6)
            );
            const s = await getDocs(qRel);
            const rel = s.docs
              .filter((d) => d.id !== id)
              .map((d) => ({ id: d.id, ...d.data() }));
            setRelated(rel);
          } catch (e) {
            console.warn("Related fetch failed", e);
          }
        }
      } catch (err) {
        console.error("Product fetch error", err);
      }
    }
    load();
  }, [id]);

  if (product === null) {
    return (
      <div style={{ textAlign: "center", padding: 40 }}>
        Product not found.
      </div>
    );
  }
  if (!product) {
    return (
      <div style={{ textAlign: "center", padding: 40 }}>
        Loading...
      </div>
    );
  }

  const images =
    product.images ||
    (product.image ? [product.image] : ["/images/placeholder.jpg"]);

  function addToCart() {
    if ((product.sizes?.length || 0) && !selectedSize) {
      return alert("Please select a size.");
    }

    const cart = JSON.parse(localStorage.getItem("cart")) || [];
    const idx = cart.findIndex((c) => c.id === id && c.size === selectedSize);

    if (idx >= 0) {
      cart[idx].qty = (cart[idx].qty || 1) + qty;
    } else {
      cart.push({
        id,
        name: product.name,
        price: product.price,
        image: images[0],
        size: selectedSize || null,
        qty,
      });
    }

    localStorage.setItem("cart", JSON.stringify(cart));
    try {
      window.dispatchEvent(new Event("storage"));
    } catch {}
    alert("Added to cart.");
  }

  function buyNow() {
    if ((product.sizes?.length || 0) && !selectedSize) {
      return alert("Please select a size.");
    }

    const buyNow = {
      id,
      name: product.name,
      price: product.price,
      image: images[0],
      size: selectedSize || null,
      qty,
    };

    sessionStorage.setItem("kwicth_buy_now", JSON.stringify(buyNow));
    navigate("/checkout", { state: { from: "buyNow" } });
  }

  return (
    <main className="pd-wrapper">
      <div className="pd-grid">
        {/* LEFT: IMAGE + THUMBNAILS */}
        <div className="pd-left">
          <div className="pd-main-image">
            <img
              src={mainImg}
              alt={product.name}
              onError={(e) => {
                e.currentTarget.src = "/images/placeholder.jpg";
              }}
            />
          </div>

          {images.length > 1 && (
            <div className="pd-thumbs">
              {images.map((src, i) => (
                <button
                  key={i}
                  type="button"
                  className={`pd-thumb ${src === mainImg ? "active" : ""}`}
                  onClick={() => setMainImg(src)}
                >
                  <img
                    src={src}
                    alt={`${product.name} ${i + 1}`}
                    onError={(e) => {
                      e.currentTarget.src = "/images/placeholder.jpg";
                    }}
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* RIGHT: DETAILS */}
        <aside className="pd-right">
          <h1 className="pd-title">{product.name}</h1>
          <div className="pd-price">₹{product.price}</div>
          <div className="pd-desc">{product.description}</div>

          {/* Sizes */}
          <div className="pd-sizes">
            {product.sizes?.length ? (
              <>
                <div className="pd-sizes-label">Select size</div>
                <div className="pd-sizes-list">
                  {product.sizes.map((sz) => (
                    <button
                      key={sz}
                      type="button"
                      className={`size-btn ${
                        selectedSize === sz ? "selected" : ""
                      }`}
                      onClick={() => setSelectedSize(sz)}
                    >
                      {sz}
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <div className="no-sizes">One size</div>
            )}
          </div>

          {/* Quantity */}
          <div className="pd-qty">
            <button
              type="button"
              onClick={() => setQty((q) => Math.max(1, q - 1))}
            >
              −
            </button>
            <div className="pd-qty-val">{qty}</div>
            <button type="button" onClick={() => setQty((q) => q + 1)}>
              +
            </button>
          </div>

          {/* Buttons */}
          <div className="pd-actions">
            <button className="pd-buy-btn" type="button" onClick={buyNow}>
              Buy Now
            </button>
            <button className="pd-add-btn" type="button" onClick={addToCart}>
              Add to Cart
            </button>
          </div>

          <div style={{ marginTop: 12 }}>
            <Link to="/products" className="pd-back">
              ← Back to products
            </Link>
          </div>
        </aside>
      </div>

      {/* Related products */}
      {related?.length > 0 && (
        <section className="pd-related">
          <h3>Related products</h3>
          <div className="pd-related-grid">
            {related.map((r) => {
              const relImg =
                (r.images && r.images[0]) ||
                r.image ||
                "/images/placeholder.jpg";
              return (
                <Link to={`/product/${r.id}`} key={r.id} className="rel-card">
                  <img
                    src={relImg}
                    alt={r.name}
                    onError={(e) => {
                      e.currentTarget.src = "/images/placeholder.jpg";
                    }}
                  />
                  <div className="rel-name">{r.name}</div>
                  <div className="rel-price">₹{r.price}</div>
                </Link>
              );
            })}
          </div>
        </section>
      )}
    </main>
  );
}

export default ProductDetail;
